import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AlertTriangle,
  Zap,
  Radiation,
  Droplets,
  Flame,
  Shield,
  X,
  ChevronRight,
  Timer,
  Activity
} from 'lucide-react'
import toast from 'react-hot-toast'

/**
 * ChaosMode Component
 * Manages random emergency push notifications with quick decision prompts
 * Simulates real emergency alerts to test user preparedness
 */

// Emergency scenarios pool
const emergencyScenarios = [
  {
    id: 'power-outage',
    type: 'blackout',
    icon: Zap,
    color: 'yellow',
    title: 'POWER GRID FAILURE',
    message: 'City-wide blackout detected. Emergency lighting activated.',
    question: 'What\'s your first move?',
    options: [
      { text: 'Find flashlight', correct: true, feedback: 'Smart! Light is priority #1' },
      { text: 'Check social media', correct: false, feedback: 'Save battery! Phone is for emergencies' },
      { text: 'Light candles everywhere', correct: false, feedback: 'Fire hazard! Use flashlights first' }
    ],
    timeLimit: 15
  },
  {
    id: 'radiation-leak',
    type: 'nuclear',
    icon: Radiation,
    color: 'green',
    title: 'RADIATION ALERT',
    message: 'Radiation spike detected 10km away. Shelter advised.',
    question: 'Quick! Where do you shelter?',
    options: [
      { text: 'Basement or center of building', correct: true, feedback: 'Perfect! Maximum protection' },
      { text: 'Car to drive away', correct: false, feedback: 'Traffic jams trap you outside!' },
      { text: 'Top floor with windows', correct: false, feedback: 'Worst spot! Radiation rises' }
    ],
    timeLimit: 10
  },
  {
    id: 'water-contamination',
    type: 'water',
    icon: Droplets,
    color: 'blue',
    title: 'WATER CONTAMINATION',
    message: 'Municipal water supply compromised. Do not drink tap water.',
    question: 'How do you secure water?',
    options: [
      { text: 'Fill containers before shutoff', correct: true, feedback: 'Quick thinking! Stock up now' },
      { text: 'Boil tap water', correct: false, feedback: 'Boiling doesn\'t remove chemicals!' },
      { text: 'Wait for government water trucks', correct: false, feedback: 'Could take days. Act now!' }
    ],
    timeLimit: 20
  },
  {
    id: 'wildfire-approach',
    type: 'fire',
    icon: Flame,
    color: 'red',
    title: 'WILDFIRE WARNING',
    message: 'Fast-moving fire 5km away. Evacuation may be needed.',
    question: 'Prepare to evacuate. What\'s essential?',
    options: [
      { text: 'Documents, meds, water', correct: true, feedback: 'Yes! The survival trinity' },
      { text: 'Photo albums and jewelry', correct: false, feedback: 'Sentimental, but not survival' },
      { text: 'All electronics and games', correct: false, feedback: 'Your Xbox won\'t save your life' }
    ],
    timeLimit: 25
  },
  {
    id: 'chemical-spill',
    type: 'chemical',
    icon: Shield,
    color: 'purple',
    title: 'CHEMICAL HAZARD',
    message: 'Industrial accident reported. Toxic fumes spreading.',
    question: 'How do you seal your shelter?',
    options: [
      { text: 'Tape windows, doors, vents', correct: true, feedback: 'Correct! Create an air seal' },
      { text: 'Open windows for ventilation', correct: false, feedback: 'NO! That lets toxins in!' },
      { text: 'Turn on all fans', correct: false, feedback: 'Fans spread contamination!' }
    ],
    timeLimit: 15
  }
]

const ChaosMode = ({ enabled = true, minDelay = 300000, maxDelay = 900000 }) => { // 5-15 min default
  const [activeAlert, setActiveAlert] = useState(null)
  const [timeRemaining, setTimeRemaining] = useState(null)
  const [selectedOption, setSelectedOption] = useState(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [chaosScore, setChaosScore] = useState(0)
  const [totalAlerts, setTotalAlerts] = useState(0)
  const [nextAlertTime, setNextAlertTime] = useState(null)

  // Initialize chaos mode
  useEffect(() => {
    if (enabled) {
      scheduleNextAlert()
    }
    
    return () => {
      // Cleanup any pending alerts
      if (nextAlertTime) {
        clearTimeout(nextAlertTime)
      }
    }
  }, [enabled])

  // Timer countdown
  useEffect(() => {
    if (activeAlert && timeRemaining > 0 && !showFeedback) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1)
      }, 1000)

      // Auto-fail if time runs out
      if (timeRemaining === 1) {
        handleTimeout()
      }

      return () => clearTimeout(timer)
    }
  }, [activeAlert, timeRemaining, showFeedback])

  /**
   * Schedules the next random alert
   */
  const scheduleNextAlert = () => {
    const delay = Math.random() * (maxDelay - minDelay) + minDelay
    const timeout = setTimeout(triggerRandomAlert, delay)
    setNextAlertTime(timeout)
  }

  /**
   * Triggers a random emergency alert
   */
  const triggerRandomAlert = () => {
    // Don't trigger if one is already active
    if (activeAlert) {
      scheduleNextAlert()
      return
    }

    // Pick random scenario
    const scenario = emergencyScenarios[Math.floor(Math.random() * emergencyScenarios.length)]
    
    // Show notification
    playAlertSound()
    setActiveAlert(scenario)
    setTimeRemaining(scenario.timeLimit)
    setTotalAlerts(prev => prev + 1)
    
    // Show system notification if supported
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('⚠️ EMERGENCY ALERT', {
        body: scenario.title,
        icon: '/icon-192x192.png',
        requireInteraction: true
      })
    }
  }

  /**
   * Plays alert sound (simulated)
   */
  const playAlertSound = () => {
    // In production, play actual alert sound
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhByw')
    audio.volume = 0.3
    audio.play().catch(() => {})
  }

  /**
   * Handles user's answer selection
   */
  const handleAnswer = (option) => {
    setSelectedOption(option)
    setShowFeedback(true)

    // Update score
    if (option.correct) {
      setChaosScore(prev => prev + 1)
      toast.success('Correct! You might just survive.')
    } else {
      toast.error('Wrong choice! Learn from this.')
    }

    // Close alert after feedback
    setTimeout(() => {
      setActiveAlert(null)
      setSelectedOption(null)
      setShowFeedback(false)
      scheduleNextAlert()
    }, 3000)
  }

  /**
   * Handles timeout (no answer)
   */
  const handleTimeout = () => {
    setShowFeedback(true)
    toast.error('Too slow! In real emergencies, hesitation kills.')
    
    setTimeout(() => {
      setActiveAlert(null)
      setShowFeedback(false)
      scheduleNextAlert()
    }, 2000)
  }

  /**
   * Dismisses alert (counts as wrong)
   */
  const dismissAlert = () => {
    toast.warning('Ignoring emergencies won\'t make them go away!')
    setActiveAlert(null)
    scheduleNextAlert()
  }

  if (!activeAlert) return null

  const Icon = activeAlert.icon
  const progress = (timeRemaining / activeAlert.timeLimit) * 100

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: -50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: -50 }}
        className="fixed top-4 right-4 w-96 z-50"
        style={{ maxWidth: 'calc(100vw - 2rem)' }}
      >
        <div className={`bg-gray-900 rounded-xl shadow-2xl border-2 overflow-hidden ${
          showFeedback && selectedOption?.correct ? 'border-green-500' : 
          showFeedback && !selectedOption?.correct ? 'border-red-500' : 
          `border-${activeAlert.color}-500`
        }`}>
          {/* Alert Header */}
          <div className={`bg-${activeAlert.color}-600 p-4 relative`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="bg-white bg-opacity-20 p-2 rounded-lg"
                >
                  <Icon className="w-6 h-6 text-white" />
                </motion.div>
                <div>
                  <h3 className="font-bold text-white text-lg">
                    {activeAlert.title}
                  </h3>
                  <p className="text-white text-opacity-90 text-sm">
                    CHAOS MODE ALERT
                  </p>
                </div>
              </div>
              <button
                onClick={dismissAlert}
                className="text-white text-opacity-60 hover:text-opacity-100 transition-opacity"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Timer Bar */}
            {!showFeedback && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-black bg-opacity-20">
                <motion.div
                  className="h-full bg-white bg-opacity-60"
                  initial={{ width: '100%' }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 1, ease: 'linear' }}
                />
              </div>
            )}
          </div>

          {/* Alert Content */}
          <div className="p-4">
            {/* Scenario */}
            <p className="text-gray-300 mb-4">
              {activeAlert.message}
            </p>

            {/* Question */}
            <div className="bg-gray-800 rounded-lg p-3 mb-4">
              <p className="font-semibold text-white flex items-center gap-2">
                <Timer className="w-4 h-4 text-orange-400" />
                {activeAlert.question}
              </p>
            </div>

            {/* Options */}
            {!showFeedback ? (
              <div className="space-y-2">
                {activeAlert.options.map((option, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAnswer(option)}
                    className="w-full text-left p-3 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <span className="text-white">{option.text}</span>
                  </motion.button>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-lg ${
                  selectedOption?.correct ? 'bg-green-900' : 'bg-red-900'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {selectedOption?.correct ? (
                    <>
                      <Activity className="w-5 h-5 text-green-400" />
                      <span className="font-bold text-green-400">CORRECT!</span>
                    </>
                  ) : (
                    <>
                      <X className="w-5 h-5 text-red-400" />
                      <span className="font-bold text-red-400">WRONG!</span>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-300">
                  {selectedOption?.feedback || 'Time\'s up! Act faster next time.'}
                </p>
              </motion.div>
            )}

            {/* Timer Display */}
            {!showFeedback && (
              <div className="mt-4 text-center">
                <span className={`text-2xl font-bold ${
                  timeRemaining <= 5 ? 'text-red-400' : 'text-orange-400'
                }`}>
                  {timeRemaining}s
                </span>
              </div>
            )}

            {/* Score Display */}
            <div className="mt-4 pt-4 border-t border-gray-800 flex justify-between text-sm">
              <span className="text-gray-400">
                Chaos Score: {chaosScore}/{totalAlerts}
              </span>
              <span className="text-gray-400">
                {Math.round((chaosScore / Math.max(totalAlerts, 1)) * 100)}% Ready
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

// Hook to manage Chaos Mode state
export const useChaosMode = () => {
  const [enabled, setEnabled] = useState(false)
  const [initialized, setInitialized] = useState(false)

  // Initialize from localStorage after component mounts (client-side only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const chaosMode = localStorage.getItem('chaosMode')
      setEnabled(chaosMode !== 'false')
      setInitialized(true)
    }
  }, [])

  const toggleChaosMode = () => {
    const newState = !enabled
    setEnabled(newState)
    
    if (typeof window !== 'undefined') {
      localStorage.setItem('chaosMode', newState.toString())
    }
    
    if (newState) {
      toast.success('Chaos Mode activated! Expect the unexpected.')
      
      // Request notification permission
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission()
      }
    } else {
      toast.success('Chaos Mode deactivated. Stay prepared!')
    }
  }

  return { enabled: initialized ? enabled : false, toggleChaosMode }
}

export default ChaosMode